import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ControlledForm from "~/shared/form/ControlledForm";
import TextInput from "~/shared/form/TextInput";
import { useForm } from "react-hook-form";
import SelectInput from "~/shared/form/SelectInput";
import FormResponse from "~/shared/form/FormResponse";
import MultiSelectInput from "~/shared/form/MultiSelectInput";
import FormButton from "~/shared/form/FormButton";
import { SegmentedMessage } from "sms-segments-calculator";

export default function SendNotification() {
    const { state } = useLocation();
    const formHook = useForm({ defaultValues });
    const { watch } = formHook;
    const [programs, setPrograms] = useState([]);

    async function defaultValues() {
        const response = await axios.get("programs");
        setPrograms(response.data);

        if (state) {
            return state;
        }

        return { programs: ["(Not in Program)", ...response.data.map(p => p.name)] };
    }

    function getNotificationTypes() {
        if (watch("roles") === "Staff") {
            return ["Email"];
        } else if (watch("roles") === "Family & Staff") {
            return ["Email", "Email & SMS"];
        }

        return ["Email", "SMS", "Email & SMS"];
    }

    async function onSubmit(data) {
        data.roles = data.roles.split(" & ").map(r => r.toUpperCase());
        data.types = data.types?.split(" & ") ?? ["Email"];

        if (data.programs) {
            data.programs = data.programs.map(p => programs.find(({ name }) => p === name));
        }
    }

    async function onSuccess(response, _, data) {
        let message = "Notification sent successfully!";

        if (!response) {
            formHook.setError("root.formResponse", { type: "success", message });
            return;
        }

        const { successful, optedOut, failed, total } = response;
        if (failed === total) {
            const message = data.types.includes("Email") ? "Sent notification via email successfully, but failed to send via SMS." : "Failed to send notification.";
            formHook.setError("root.formResponse", { type: "error", message });
            return;
        }

        if (successful > 0) {
            message += ` ${successful} SMS ${successful === 1 ? "recipient" : "recipients"} received a message.`;
        }
        if (optedOut > 0) {
            message += ` ${optedOut}/${total} SMS recipients ${successful === 1 ? "was" : "were"} skipped due to having unsubscribed.`;
        }
        if (failed > 0) {
            message += ` ${failed}/${total} SMS recipients did not receive a message due to an unknown error.`;
        }
        formHook.setError("root.formResponse", { type: "success", message });
    }

    function calculateMessageLength(value) {
        const message = new SegmentedMessage(value, "auto", true);
        return `${message.numberOfCharacters}${message.segmentsCount > 1 ? ` (${message.segmentsCount} segments)` : ""}`;
    }

    return (
        <section>
            <ControlledForm className="flex flex-col" hook={formHook} onSubmit={onSubmit} onSuccess={onSuccess} post="notifications/send" resetOnSuccess={["title", "message"]} noAnimation={!state}>
                <div className="flex flex-col gap-3">
                    <SelectInput label="Filter by Role" id="roles" options={["Family", "Staff", "Family & Staff"]} />
                    <SelectInput label="Notification Type" deps={["title", "message"]} id="types" options={getNotificationTypes()} disabled={watch("roles") === "Staff"}>
                        {watch("roles") !== "Family" && <span className="text-neutral-600 text-sm text-left mt-1">Staff will not receive SMS notifications.</span>}
                    </SelectInput>
                    {watch("roles") !== "Staff" && (
                        <MultiSelectInput label="Programs" id="programs" invalid="At least one program must be selected." placeholder="Select programs..." options={["(Not in Program)", ...programs.map(p => p.name)]} required>
                            <span className="text-neutral-600 text-sm text-left mt-1">The notification will only be sent to families in the selected programs.</span>
                        </MultiSelectInput>
                    )}
                    <TextInput label="Title" id="title" required>
                        {watch("types") !== "Email" && <span className="text-neutral-600 text-sm text-left mt-1">Title will not be sent in SMS notifications, but will still be stored in notification logs.</span>}
                    </TextInput>
                    <TextInput label="Message" id="message" multiline showLength={watch("types") !== "Email"} calculateLength={calculateMessageLength} rows={4} required />
                </div>
                <FormResponse className="mt-3 mb-0" />
                <FormButton legacy submit className="mt-4">Send Notification</FormButton>
            </ControlledForm>
        </section>
    );
}
